/**
 * migration完了後にDB全体の外部キー整合性を検査し、CASCADE違反行を修復する。
 *
 * migration中の検査 (index.ts) はmigration自身のバグ検出が目的で、既存違反では
 * chainを止めない。そのため既存違反を掃除する経路がここに必要になる。
 *
 * 削除対象の判定には自作の述語を使わず `PRAGMA foreign_key_check` の出力を正解として
 * rowid指定で削除する。NULL許容列・複合列・型親和性の解釈ミスが原理的に起こらない。
 */
import type { RunToDatabase } from '@/types'
import type { DatabaseWithTransaction } from '@pkg/database-portfolio'
import { sql } from 'drizzle-orm'
import { z } from 'zod'

/** `PRAGMA foreign_key_check` の1行 `(table, rowid, parent, fkid)`。 */
const foreignKeyCheckRowSchema = z.tuple([
  z.string(),
  z.union([z.number(), z.bigint(), z.null()]),
  z.string(),
  z.union([z.number(), z.bigint()]),
])

/** `pragma_foreign_key_list` から取得する on_delete 動作。 */
const onDeleteRowsSchema = z.array(z.tuple([z.string()]))

/** FK制約を構成する子側column名の一覧。 */
const fromColumnRowsSchema = z.array(z.tuple([z.string()]))

/** 削除した行を識別するための最小限の情報。 */
export type RepairedForeignKeyRow = {
  /** 違反行を持つtable名 */
  readonly table: string
  /** 参照先table名 */
  readonly parent: string
  /** 削除した行のFK column値。財務値は含めない */
  readonly foreignKeyValues: Readonly<Record<string, unknown>>
}

/** 削除せず報告だけ行った違反。 */
export type SkippedForeignKeyViolation = {
  /** 違反行を持つtable名 */
  readonly table: string
  /** 参照先table名 */
  readonly parent: string
  /** 削除しなかった理由 */
  readonly reason: 'non-cascade' | 'missing-rowid'
  /** FK制約の on_delete 動作 */
  readonly onDelete: string | undefined
}

/** 外部キー修復の結果。 */
export type ForeignKeyRepairResult = {
  /** rowid指定で直接削除した行の合計件数 */
  readonly deletedCount: number
  /** relation (`child -> parent`) ごとの直接削除件数 */
  readonly deletedCountByRelation: Readonly<Record<string, number>>
  /** 直接削除した行の識別情報 */
  readonly deletedRows: readonly RepairedForeignKeyRow[]
  /**
   * table別に実際に減少した行数。
   * FK強制が有効な状態では直接削除がCASCADEで子孫へ連鎖するため、
   * deletedCountより多くの行が失われる。バックアップ照合にはこちらを使う。
   */
  readonly removedRowsByTable: Readonly<Record<string, number>>
  /** CASCADE連鎖を含めて実際に減少した行数の合計 */
  readonly removedRowsTotal: number
  /** 削除せず残した違反 */
  readonly skipped: readonly SkippedForeignKeyViolation[]
  /** 修復に要したround数 */
  readonly rounds: number
  /** round上限に達しても違反が残った場合はtrue */
  readonly unconverged: boolean
}

/** cascade連鎖で新たな違反が生じるため、収束するまで繰り返す上限。 */
const MAX_REPAIR_ROUNDS = 10

/** 報告payloadを有界に保つため記録する行数の上限。 */
const MAX_RECORDED_ROWS = 50

/**
 * 指定FK制約の on_delete 動作を取得する。
 * @param params - database: 接続先、table: 子table名、fkId: FK制約ID。
 * @returns on_delete動作。取得できなければundefined。
 */
const getForeignKeyOnDelete = async ({
  database,
  table,
  fkId,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly table: string
  readonly fkId: number
}): Promise<string | undefined> => {
  const rows = onDeleteRowsSchema.safeParse(
    await database.values(
      sql`SELECT on_delete FROM pragma_foreign_key_list(${table}) WHERE id = ${fkId} LIMIT 1;`,
    ),
  )
  return rows.success ? rows.data[0]?.[0] : undefined
}

/**
 * 指定FK制約を構成する子側column名を取得する。
 * @param params - database: 接続先、table: 子table名、fkId: FK制約ID。
 * @returns column名の配列。
 */
const getForeignKeyFromColumns = async ({
  database,
  table,
  fkId,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly table: string
  readonly fkId: number
}): Promise<readonly string[]> => {
  const rows = fromColumnRowsSchema.safeParse(
    await database.values(
      sql`SELECT "from" FROM pragma_foreign_key_list(${table}) WHERE id = ${fkId} ORDER BY seq;`,
    ),
  )
  return rows.success ? rows.data.map(([column]) => column) : []
}

/**
 * 削除前に、行を識別するFK column値だけを取得する。
 * 金額などの財務値を報告payloadへ載せないため対象columnを限定する。
 * @param params - database: 接続先、table: table名、rowId: 対象rowid、columns: 取得するcolumn。
 * @returns column名と値の対応。取得できなければ空object。
 */
const readForeignKeyValues = async ({
  database,
  table,
  rowId,
  columns,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly table: string
  readonly rowId: number
  readonly columns: readonly string[]
}): Promise<Readonly<Record<string, unknown>>> => {
  if (columns.length === 0) {
    return {}
  }
  const selectList = sql.raw(
    columns.map((column) => `"${column.replaceAll('"', '""')}"`).join(', '),
  )
  const tableName = sql.raw(`"${table.replaceAll('"', '""')}"`)
  const rows = await database.values(
    sql`SELECT ${selectList} FROM ${tableName} WHERE rowid = ${rowId} LIMIT 1;`,
  )
  const row = rows[0]
  if (!Array.isArray(row)) {
    return {}
  }
  const values: Record<string, unknown> = {}
  for (const [index, column] of columns.entries()) {
    values[column] = row[index]
  }
  return values
}

/** 全user tableの行数を検証するschema。 */
const tableCountRowsSchema = z.array(z.tuple([z.string(), z.number()]))

/**
 * 全user tableの行数を取得する。CASCADE連鎖を含む実削除数の算出に使う。
 * 違反が存在する場合だけ呼ぶことで、平常時のコストを発生させない。
 * @param params - database: 接続先。
 * @returns table名ごとの行数。
 */
const readTableRowCounts = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<ReadonlyMap<string, number>> => {
  const tableRows = await database.values(
    sql`SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%';`,
  )
  const counts = new Map<string, number>()
  for (const tableRow of tableRows) {
    const parsedName = z.tuple([z.string()]).safeParse(tableRow)
    if (!parsedName.success) {
      continue
    }
    const table = parsedName.data[0]
    const tableName = sql.raw(`"${table.replaceAll('"', '""')}"`)
    const countRows = tableCountRowsSchema.safeParse(
      await database.values(
        sql`SELECT ${table} AS name, COUNT(*) AS count FROM ${tableName};`,
      ),
    )
    if (countRows.success && countRows.data[0] !== undefined) {
      counts.set(table, countRows.data[0][1])
    }
  }
  return counts
}

/**
 * 修復前後のtable行数から、実際に減少した行数を算出する。
 * @param params - before: 修復前の行数、after: 修復後の行数。
 * @returns 減少したtableと減少件数。
 */
const diffTableRowCounts = ({
  before,
  after,
}: {
  readonly before: ReadonlyMap<string, number>
  readonly after: ReadonlyMap<string, number>
}): Readonly<Record<string, number>> => {
  const removed: Record<string, number> = {}
  for (const [table, beforeCount] of before) {
    const afterCount = after.get(table)
    if (afterCount !== undefined && afterCount < beforeCount) {
      removed[table] = beforeCount - afterCount
    }
  }
  return removed
}

/**
 * DB全体の外部キー違反のうちCASCADE指定のものを削除し、整合性を回復する。
 *
 * CASCADE違反行は「FK強制が有効なら親削除時に自動削除されていた残骸」であり、
 * 削除しても意味的な情報は失われない。NO ACTION違反は別種の異常のため削除せず報告する。
 *
 * @param params - readFromDatabase: 読み取り実行関数、writeToDatabase: 書き込み実行関数。
 * @returns 削除件数と削除行の識別情報、削除しなかった違反の一覧。
 */
export const repairForeignKeyViolations = async ({
  readFromDatabase,
  writeToDatabase,
}: {
  readonly readFromDatabase: RunToDatabase
  readonly writeToDatabase: RunToDatabase
}): Promise<ForeignKeyRepairResult> => {
  const deletedCountByRelation: Record<string, number> = {}
  const deletedRows: RepairedForeignKeyRow[] = []
  const skipped: SkippedForeignKeyViolation[] = []
  let deletedCount = 0
  let rounds = 0
  // 違反ゼロ、または CASCADE で修復できる違反が尽きた時点を収束とみなす
  let converged = false
  // FK定義はmigration完了後に変化しないため、違反行ごとの再取得を避ける
  const onDeleteCache = new Map<string, string | undefined>()
  const fromColumnsCache = new Map<string, readonly string[]>()
  // 違反が見つかった場合だけ取得する。平常時のコストを発生させないため遅延取得する
  let rowCountsBeforeRepair: ReadonlyMap<string, number> | undefined

  for (let round = 1; round <= MAX_REPAIR_ROUNDS; round += 1) {
    rounds = round
    const violationRows = await readFromDatabase(
      async (database) =>
        await database.database.values(sql`PRAGMA foreign_key_check;`),
    )
    if (violationRows.length === 0) {
      converged = true
      break
    }
    if (rowCountsBeforeRepair === undefined) {
      rowCountsBeforeRepair = await readFromDatabase(
        async (database) =>
          await readTableRowCounts({ database: database.database }),
      )
    }

    let deletedInRound = 0
    // roundごとにskippedを作り直し、収束時点の未修復違反だけを最終結果に残す
    skipped.length = 0

    for (const violationRow of violationRows) {
      const parsedRow = foreignKeyCheckRowSchema.safeParse(violationRow)
      if (!parsedRow.success) {
        skipped.push({
          table: 'unparsable row',
          parent: 'unknown',
          reason: 'missing-rowid',
          onDelete: undefined,
        })
        continue
      }
      const [table, rowId, parent, fkId] = parsedRow.data
      const relation = `${table} -> ${parent}`

      const foreignKeyCacheKey = `${table}#${fkId}`
      const onDelete = onDeleteCache.has(foreignKeyCacheKey)
        ? onDeleteCache.get(foreignKeyCacheKey)
        : await readFromDatabase(async (database) => {
            const value = await getForeignKeyOnDelete({
              database: database.database,
              table,
              fkId: Number(fkId),
            })
            onDeleteCache.set(foreignKeyCacheKey, value)
            return value
          })
      if (onDelete !== 'CASCADE') {
        skipped.push({ table, parent, reason: 'non-cascade', onDelete })
        continue
      }
      if (rowId === null) {
        skipped.push({ table, parent, reason: 'missing-rowid', onDelete })
        continue
      }

      const targetRowId = Number(rowId)
      // 報告payloadの上限に達した後はFK値の取得自体を省き、削除だけを続ける
      const shouldRecordRow = deletedRows.length < MAX_RECORDED_ROWS
      const foreignKeyValues = shouldRecordRow
        ? await readFromDatabase(async (database) => {
            const cachedColumns = fromColumnsCache.get(foreignKeyCacheKey)
            const columns =
              cachedColumns ??
              (await getForeignKeyFromColumns({
                database: database.database,
                table,
                fkId: Number(fkId),
              }))
            fromColumnsCache.set(foreignKeyCacheKey, columns)
            return await readForeignKeyValues({
              database: database.database,
              table,
              rowId: targetRowId,
              columns,
            })
          })
        : {}

      await writeToDatabase(async (database) => {
        const tableName = sql.raw(`"${table.replaceAll('"', '""')}"`)
        await database.database.run(
          sql`DELETE FROM ${tableName} WHERE rowid = ${targetRowId};`,
        )
      })

      deletedCount += 1
      deletedInRound += 1
      deletedCountByRelation[relation] =
        (deletedCountByRelation[relation] ?? 0) + 1
      if (shouldRecordRow) {
        deletedRows.push({ table, parent, foreignKeyValues })
      }
    }

    // CASCADE で修復できる違反が尽きた。残りは削除対象外なので収束とみなす
    if (deletedInRound === 0) {
      converged = true
      break
    }
  }

  // CASCADE連鎖で消えた行を含む実削除数を、修復前後の行数差から算出する
  const removedRowsByTable =
    rowCountsBeforeRepair === undefined
      ? {}
      : diffTableRowCounts({
          before: rowCountsBeforeRepair,
          after: await readFromDatabase(
            async (database) =>
              await readTableRowCounts({ database: database.database }),
          ),
        })

  return {
    deletedCount,
    deletedCountByRelation,
    deletedRows,
    removedRowsByTable,
    removedRowsTotal: Object.values(removedRowsByTable).reduce(
      (total, count) => total + count,
      0,
    ),
    skipped,
    rounds,
    // round 上限に達しても削除が続いていた場合だけ未収束として報告する
    unconverged: !converged,
  }
}
